import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/** "21 m/s" or "21m/s" is a basic wind velocity. A plain number is a line load in kN/m. */
export function parseWindEntry(raw) {
  const text = String(raw ?? '').trim();
  const speed = text.match(/^([+-]?\d+(?:[.,]\d+)?)\s*m\s*\/\s*s$/i);
  if (speed) return { kind: 'speed', value: Number(speed[1].replace(',', '.')) };
  const line = Number(text.replace(',', '.'));
  return { kind: 'line', value: Number.isFinite(line) ? line : 0 };
}

export const defaultTrussInputs = {
  shape: 'flat',
  pattern: 'warren',
  structure_type: 'truss_pinned',
  span_m: 12,
  height_left_m: 1.2,
  arch_rise_m: 4,
  arch_springing_height_m: 0,
  arch_system: 'beam',
  arch_curve: 'circular',
  arch_segments: 16,
  arch_depth_m: 0.8,
  arch_base_fixity: 'pinned',
  arch_column_taper: 'tapered',
  height_right_m: 0,
  ridge_offset_m: 0,
  /** Dual-pitch ridge height above horizontal bottom chord (m). */
  dual_ridge_height_m: '',
  /** @deprecated Kept for API/workspace compat; UI no longer exposes overrides. */
  dual_heel_left_m: '',
  dual_heel_right_m: '',
  dual_span_left_to_ridge_m: '',
  eccentricity_mm: 0,
  roller_on: 'right',
  /** bottom: bearings under bottom chord; top: flat truss bearings on top chord (mono/dual still use bottom). */
  support_placement: 'top',
  /** Lever arm (m) for end top-chord moment check with top bearings; mesh bays stay uniform. */
  bearing_connection_offset_m: 0.2,
  /** If true, portal column catalog selection is fixed; if false, lightest suitable HEA/IPE is auto-picked. */
  lock_column_section: false,
  column_section_type: 'HEA',
  chord_family: 'tube',
  /** HEA or IPE when chord_family === 'open' */
  open_chord_shape: 'HEA',
  manual_sections: false,
  /** Lateral buckling length: metres or '<factor>p' (= factor × panel). */
  chord_bracing_lateral_m: '1p',
  /** In-plane chord buckling length: metres or '<factor>p'. Default shown explicitly. */
  chord_bracing_truss_plane_m: '0.9p',
  tube_width_match_mm: 40,
  top_chord: { type: 'CFSHS', id: 10 },
  bottom_chord: { type: 'CFSHS', id: 10 },
  diagonal: { type: 'CFSHS', id: 10 },
  vertical: null,
  column: { type: 'HEA', id: 3 },
  column_left_m: 4,
  column_right_m: 4,
  /** Rotational spring at column base (kN·m/rad); 0 = free rotation apart from numerical stabilisation */
  column_base_rotational_stiffness_kNm_per_rad: '',
  portal_knee_top_fixity: 'pinned',
  portal_knee_bottom_fixity: 'pinned',
  second_order_p_delta: true,
  column_out_of_plane_brace_m: '',
  wind_source_mode: 'line',
  /** When true, backend uses EN 1991-1-4 zoned wind (two cases). Mutually exclusive with wind_model_manual in UI. */
  wind_model_en1991: false,
  /** Manual symmetric column + top-chord wind line loads. */
  wind_model_manual: false,
  /** Whole-building analysis (repeated frames, bracing, expansion zones). */
  building_mode: false,
  stability_system: 'concentrically_braced',
  concentrically_braced_mode: true,
  frame_spacings_m: '',
  vertical_bracing_bays: '',
  transverse_brace_frame_indices: '',
  lengthwise_brace_frame_indices: '',
  roof_bracing_bays: '',
  /** auto | manual — OCBF defaults mid lengthwise VB/HT + end transverse VB when auto */
  bracing_layout_mode: 'auto',
  wind_column_max_spacing_m: 6,
  roof_diaphragm: 'none',
  diaphragm_stiffness_kN_per_m: '',
  diaphragm_property_source: 'eccs88_calculated',
  diaphragm_profile_id: 'T130M-75L-930',
  diaphragm_c_panel_mm_per_kn: '',
  diaphragm_s_kn: '',
  diaphragm_v_rd_kn: '',
  diaphragm_panel_length_m: 6,
  diaphragm_support_span_m: 1.5,
  diaphragm_thickness_mm: 0.75,
  diaphragm_edge_member_area_mm2: 800,
  diaphragm_sls_drift_limit_mm: 25,
  diaphragm_source_document: '',
  diaphragm_na_permitted: false,
  diaphragm_structural_ack: false,
  diaphragm_opening_ratio: 0,
  stressed_skin_member_restraint: false,
  diaphragm_member_restraint_spacing_m: '',
  sn027_bottom_chord_check: false,
  sn027_purlin_I_m4: '',
  sn027_web_I_m4: '',
  sn027_screw_eccentricity_m: 0.19,
  analysis_stage: 'completed',
  design_profile: 'sfs_en',
  solver_mode: 'staged',
  schema_version: 2,
  building_length_m: 12,
  terrain_category: 'II',
  wind_manual_column_kN_m: 0,
  wind_manual_top_chord_kN_m: 0,
  portal_column_subdivisions: 6,
  /** SLS lateral drift limit: column height / this ratio (e.g. 150). */
  drift_limit_ratio: 150,
  frame_spacing_m: 6,
  wind_speed_ms: 21,
  wind_speed_mph: 110,
  wind_column_left_kN_m: 0,
  wind_column_right_kN_m: 0,
  wind_roof_pressure_kN_m: 0,
  wind_roof_suction_kN_m: 0,
  wind_direction: 'envelope',
  /** Number of diagonal section groups (1–10), default 3 for optimisation */
  diagonal_group_count: 3,
  top_chord_left: null,
  top_chord_right: null,
  diagonal_2: null,
  diagonal_3: null,
  g_kN_m: 0.5,
  /** Area loads for whole-building mode (converted to kN/m via frame spacing in API payload). */
  g_kN_m2: 0.083,
  snow_kN_m: 1.0,
  /** EN 1991-1-3 snow from s_k + C_e and roof shape (Table 5.2). */
  snow_model_en1991: false,
  sk_kN_m2: 2.0,
  snow_Ce: 1.0,
  snow_Ct: 1.0,
  /** manual | coordinates — Finnish NAF map (YM 6/16) when coordinates */
  snow_sk_source: 'manual',
  site_coord_system: 'wgs84',
  site_lat: '',
  site_lon: '',
  site_northing: '',
  site_easting: '',
  /** ULS: envelope member checks with alternating top-chord snow panels (uniform + two patch phases). */
  snow_checkerboard_ul: false,
  imposed_kN_m: 0,
  imposed_kN_m2: 0,
  wind_kN_m: 0,
  bottom_chord_kN_m: 0.2,
  self_weight: true,
  optimize: false,
  utilization_target: 0.9,
  /** Auto-design objective: total EUR = kg × this + overlap_bottom_joints × cost_eur_per_overlap_joint */
  cost_eur_per_installed_steel_kg: 2,
  cost_eur_per_overlap_joint: 10,
  deflection_limit_ratio: 200,
  /** Member + joint capacity checks: eurocode | aisc_lrfd */
  design_code: 'eurocode',
  national_annex: 'FI',
  /** Half-sine chord bow in global FEM: amplitude = span_m / chord_imperfection_span_ratio (default 500) */
  chord_imperfection_include: true,
  chord_imperfection_span_ratio: 500,
  /** EN 1993-1-1 §5.3.2(4) equivalent sway loads on portal (ULS only) */
  portal_include_sway_imperfection: true,
  /** When true (default), analysis returns per-node K-joint debug PNGs (matplotlib). */
  joint_debug_plots: true,
  /** Overlap K-joint: count chord-face heel weld on overlapped (under) brace in L_eff (EN 1993-1-8 §7.1.5(8) often unwelded). */
  overlap_hidden_seam_welded: false,
};

const TrussContext = createContext(null);

export function TrussProvider({ children }) {
  const [inputs, setInputs] = useState(() => ({ ...defaultTrussInputs }));
  const [results, setResults] = useState(null);
  const [previewGeometry, setPreviewGeometry] = useState(null);
  const [previewBracingLayout, setPreviewBracingLayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resultsOutOfDate, setResultsOutOfDate] = useState(true);

  const updateInputs = useCallback((patch) => {
    setInputs((prev) => ({ ...prev, ...patch }));
    setResultsOutOfDate(true);
  }, []);

  const buildApiPayload = useCallback(() => {
    const i = inputs;
    const building = !!i.building_mode;
    const singleBeamArch =
      !building && i.shape === 'arch' && (i.arch_system || 'beam') !== 'truss';
    const cbf =
      !singleBeamArch &&
      (!!i.concentrically_braced_mode || i.stability_system === 'concentrically_braced');
    const frameSpacing = Math.max(Number(i.frame_spacing_m ?? 6), 0.1);
    const gLine = building ? Number(i.g_kN_m2 ?? 0) * frameSpacing : Number(i.g_kN_m);
    const imposedLine = building ? Number(i.imposed_kN_m2 ?? 0) * frameSpacing : Number(i.imposed_kN_m);
    const snowEn = building && i.design_code !== 'aisc_lrfd' ? true : !!i.snow_model_en1991;
    const windEntry = parseWindEntry(i.wind_kN_m);
    const windSpeedTyped = windEntry.kind === 'speed' && i.design_code !== 'aisc_lrfd';
    const windEn = i.design_code !== 'aisc_lrfd' && (building || windSpeedTyped);
    const windManual = !building && !windSpeedTyped && !!i.wind_model_manual;
    const packBracing = (v) => {
      if (v === '' || v == null) return undefined;
      const t = String(v).trim();
      if (!t) return undefined;
      if (/p\s*$/i.test(t.replace(',', '.'))) return t.trim();
      const n = Number(String(t).replace(',', '.'));
      return Number.isFinite(n) ? n : t;
    };
    const brLat = packBracing(i.chord_bracing_lateral_m);
    const brPl = packBracing(i.chord_bracing_truss_plane_m);
    return {
      shape: i.shape,
      pattern: i.pattern,
      structure_type: i.structure_type,
      span_m: Number(i.span_m),
      height_left_m: Number(i.height_left_m),
      arch_rise_m:
        i.shape === 'arch'
          ? Number(i.arch_rise_m || i.height_left_m)
          : undefined,
      arch_springing_height_m: i.shape === 'arch' ? Number(i.arch_springing_height_m || 0) : undefined,
      arch_system: i.shape === 'arch' ? i.arch_system || 'beam' : undefined,
      arch_curve: i.shape === 'arch' && (i.arch_system || 'beam') === 'truss'
        ? i.arch_curve || 'circular'
        : undefined,
      arch_segments: i.shape === 'arch' ? Number(i.arch_segments || 16) : undefined,
      arch_depth_m: i.shape === 'arch' ? Number(i.arch_depth_m || 0.8) : undefined,
      arch_base_fixity: i.shape === 'arch' ? i.arch_base_fixity || 'pinned' : undefined,
      arch_column_taper:
        i.shape === 'arch' && (i.arch_system || 'beam') === 'truss'
          ? i.arch_column_taper || 'tapered'
          : undefined,
      n_panels: i.shape === 'arch' ? Number(i.arch_segments || 16) : undefined,
      height_right_m: Number(i.height_right_m),
      ridge_offset_m: Number(i.ridge_offset_m),
      // Dual: height_left/right = heels; dual_ridge_height_m = peak (linear top chord each side).
      dual_ridge_height_m:
        i.shape === 'dual' && i.dual_ridge_height_m !== '' && i.dual_ridge_height_m != null
          ? Number(i.dual_ridge_height_m)
          : undefined,
      eccentricity_mm: Number(i.eccentricity_mm),
      roller_on: i.roller_on,
      support_placement: i.support_placement || 'bottom',
      bearing_connection_offset_m: Number(i.bearing_connection_offset_m ?? 0),
      lock_column_section: !!i.lock_column_section,
      column_section_type: i.column?.type || i.column_section_type || 'HEA',
      chord_family: i.chord_family,
      open_chord_shape: i.chord_family === 'open' ? i.open_chord_shape || 'HEA' : undefined,
      manual_sections: !!i.manual_sections,
      chord_bracing_lateral_m: brLat,
      chord_bracing_truss_plane_m: brPl,
      tube_width_match_mm: Number(i.tube_width_match_mm ?? 40),
      top_chord: i.manual_sections ? i.top_chord : undefined,
      top_chord_left: i.manual_sections && i.shape === 'dual' ? i.top_chord_left || i.top_chord : undefined,
      top_chord_right: i.manual_sections && i.shape === 'dual' ? i.top_chord_right || i.top_chord : undefined,
      bottom_chord: i.manual_sections ? i.bottom_chord : undefined,
      diagonal: i.manual_sections ? i.diagonal : undefined,
      diagonal_2:
        i.manual_sections && Number(i.diagonal_group_count || 3) >= 2 ? i.diagonal_2 || i.diagonal : undefined,
      diagonal_3:
        i.manual_sections && Number(i.diagonal_group_count || 3) >= 3 ? i.diagonal_3 || i.diagonal : undefined,
      vertical: i.manual_sections ? i.vertical : undefined,
      column: i.structure_type === 'portal' || cbf ? i.column : null,
      column_left_m: Number(i.column_left_m),
      column_right_m: Number(i.column_right_m),
      column_base_rotational_stiffness_kNm_per_rad:
        i.column_base_rotational_stiffness_kNm_per_rad === '' || i.column_base_rotational_stiffness_kNm_per_rad == null
          ? undefined
          : Number(String(i.column_base_rotational_stiffness_kNm_per_rad).replace(/\s+/g, '')),
      portal_knee_top_fixity: inputs.portal_knee_top_fixity || 'pinned',
      portal_knee_bottom_fixity: inputs.portal_knee_bottom_fixity || 'pinned',
      second_order_p_delta: !!i.second_order_p_delta,
      column_out_of_plane_brace_m:
        i.column_out_of_plane_brace_m === '' || i.column_out_of_plane_brace_m == null
          ? undefined
          : i.column_out_of_plane_brace_m,
      wind_source_mode: i.wind_source_mode || 'line',
      /** Explicit routing for ``resolve_wind_cases`` (avoids relying on boolean JSON alone). */
      wind_model: windManual ? 'manual' : windEn ? 'en1991' : 'legacy',
      wind_model_en1991: windEn,
      wind_model_manual: windManual,
      building_length_m: Number(i.building_length_m ?? 12),
      terrain_category: i.terrain_category || 'II',
      wind_manual_column_kN_m: Number(i.wind_manual_column_kN_m ?? 0),
      wind_manual_top_chord_kN_m: Number(i.wind_manual_top_chord_kN_m ?? 0),
      portal_column_subdivisions: Number(i.portal_column_subdivisions ?? 6),
      drift_limit_ratio: Number(i.drift_limit_ratio ?? 150),
      frame_spacing_m: Number(i.frame_spacing_m ?? 6),
      wind_speed_ms: windSpeedTyped
        ? windEntry.value
        : !building
          ? (i.wind_speed_explicit ? Number(i.wind_speed_ms) : undefined)
          : String(i.national_annex || '').toUpperCase().startsWith('LV') &&
              (i.wind_speed_ms === '' || i.wind_speed_ms == null)
            ? undefined
            : Number(i.wind_speed_ms ?? 21),
      wind_speed_explicit: windSpeedTyped || !!i.wind_speed_explicit,
      wind_coastal_exposure: !!i.wind_coastal_exposure,
      snow_zone_is: i.snow_zone_is || '',
      imposed_category: i.imposed_category || 'H',
      wind_speed_mph: Number(i.wind_speed_mph ?? 110),
      wind_column_left_kN_m: Number(i.wind_column_left_kN_m ?? 0),
      wind_column_right_kN_m: Number(i.wind_column_right_kN_m ?? 0),
      wind_roof_pressure_kN_m: Number(i.wind_roof_pressure_kN_m ?? 0),
      wind_roof_suction_kN_m: Number(i.wind_roof_suction_kN_m ?? 0),
      wind_direction: i.wind_direction || 'envelope',
      diagonal_group_count: Number(i.diagonal_group_count ?? 3),
      g_kN_m: gLine,
      snow_model: snowEn ? 'en1991' : 'legacy',
      snow_model_en1991: snowEn,
      sk_kN_m2: (() => {
        const zoneOnly =
          String(i.national_annex || '').toUpperCase().startsWith('IS') &&
          String(i.snow_sk_source || '') === 'is_zone' &&
          !['4', 'H'].includes(String(i.snow_zone_is || '').toUpperCase());
        if (String(i.national_annex || '').toUpperCase().startsWith('LV') && (i.sk_kN_m2 === '' || i.sk_kN_m2 == null)) {
          return undefined;
        }
        return zoneOnly ? undefined : Number(i.sk_kN_m2 ?? 2);
      })(),
      snow_zone_is_sk_kN_m2:
        i.sk_kN_m2 === '' || i.sk_kN_m2 == null ? undefined : Number(i.sk_kN_m2),
      snow_Ce: Number(i.snow_Ce ?? 1),
      snow_Ct: Number(i.snow_Ct ?? 1),
      snow_sk_source: i.snow_sk_source || 'manual',
      site_coord_system: i.site_coord_system || 'wgs84',
      site_lat: i.site_lat === '' || i.site_lat == null ? undefined : Number(i.site_lat),
      site_lon: i.site_lon === '' || i.site_lon == null ? undefined : Number(i.site_lon),
      site_northing: i.site_northing === '' || i.site_northing == null ? undefined : Number(i.site_northing),
      site_easting: i.site_easting === '' || i.site_easting == null ? undefined : Number(i.site_easting),
      snow_kN_m: Number(i.snow_kN_m),
      snow_checkerboard_ul: !!i.snow_checkerboard_ul,
      imposed_kN_m: imposedLine,
      wind_kN_m: windEntry.kind === 'line' ? windEntry.value : 0,
      bottom_chord_kN_m: Number(i.bottom_chord_kN_m),
      self_weight: !!i.self_weight,
      optimize: !!i.optimize,
      utilization_target: Number(i.utilization_target),
      cost_eur_per_installed_steel_kg: Number(i.cost_eur_per_installed_steel_kg ?? 2),
      cost_eur_per_overlap_joint: Number(i.cost_eur_per_overlap_joint ?? 10),
      deflection_limit_ratio: Number(i.deflection_limit_ratio),
      design_code: i.design_code || 'eurocode',
      national_annex: i.national_annex,
      chord_imperfection_include: !!i.chord_imperfection_include,
      chord_imperfection_span_ratio: Number(i.chord_imperfection_span_ratio ?? 500),
      portal_include_sway_imperfection: !!i.portal_include_sway_imperfection,
      /** Default on: first K-joint sketch with analysis; others on demand (set false to disable entirely). */
      joint_debug_plots: i.joint_debug_plots !== false,
      overlap_hidden_seam_welded: !!i.overlap_hidden_seam_welded,
      building_mode: !!i.building_mode,
      stability_system: cbf
        ? 'concentrically_braced'
        : singleBeamArch
          ? undefined
          : (i.stability_system || 'concentrically_braced'),
      concentrically_braced_mode: cbf,
      frame_spacings_m: (() => {
        const raw = String(i.frame_spacings_m || '').trim();
        if (!raw) return undefined;
        const parts = raw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
        return parts.length ? parts : undefined;
      })(),
      transverse_brace_frame_indices: (() => {
        const raw = String(i.transverse_brace_frame_indices || i.vertical_bracing_bays || '').trim();
        if (!raw) return undefined;
        return raw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n));
      })(),
      lengthwise_brace_frame_indices: (() => {
        const raw = String(i.lengthwise_brace_frame_indices || '').trim();
        if (!raw) return undefined;
        return raw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n));
      })(),
      vertical_bracing_bays: (() => {
        const raw = String(i.transverse_brace_frame_indices || i.vertical_bracing_bays || '').trim();
        if (!raw) return undefined;
        return raw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n));
      })(),
      roof_bracing_bays: undefined,
      bracing_layout_mode: i.bracing_layout_mode || 'auto',
      wind_column_max_spacing_m: Number(i.wind_column_max_spacing_m ?? 6),
      roof_diaphragm: i.roof_diaphragm || 'none',
      diaphragm_stiffness_kN_per_m:
        i.diaphragm_stiffness_kN_per_m === '' || i.diaphragm_stiffness_kN_per_m == null
          ? undefined
          : Number(i.diaphragm_stiffness_kN_per_m),
      roof_diaphragm_detail: (() => {
        const stressedSkinOn = (i.roof_diaphragm || 'none') === 'semi_rigid';
        return {
        system_type: 'verified_assembly_override',
        property_source:
          i.diaphragm_profile_id === 'custom'
            ? 'manual_override'
            : i.diaphragm_property_source || 'eccs88_calculated',
        profile_id: i.diaphragm_profile_id || 'T130M-75L-930',
        // Selecting stressed skin implies credit; no separate acknowledgement UI.
        source_document:
          i.diaphragm_source_document ||
          (stressedSkinOn
            ? i.diaphragm_profile_id === 'custom'
              ? 'Custom diaphragm stiffness (UI)'
              : i.diaphragm_profile_id || 'T130M-75L-930'
            : ''),
        structural_credit_acknowledged: !!i.diaphragm_structural_ack,
        na_stressed_skin_permitted: !!i.diaphragm_na_permitted,
        panel_length_m: Number(i.diaphragm_panel_length_m) || 6,
        support_span_m: Number(i.diaphragm_support_span_m) || 1.5,
        thickness_mm: Number(i.diaphragm_thickness_mm) || 0.75,
        edge_member_area_mm2: Number(i.diaphragm_edge_member_area_mm2) || 0,
        opening_area_ratio: Number(i.diaphragm_opening_ratio) || 0,
        c_panel_mm_per_kn:
          i.diaphragm_c_panel_mm_per_kn === '' || i.diaphragm_c_panel_mm_per_kn == null
            ? undefined
            : Number(i.diaphragm_c_panel_mm_per_kn),
        s_kn:
          i.diaphragm_s_kn === '' || i.diaphragm_s_kn == null ? undefined : Number(i.diaphragm_s_kn),
        s_reference_length_m: Number(i.diaphragm_panel_length_m) || 6,
        v_rd_kn: Number(i.diaphragm_v_rd_kn) || 0,
        sls_drift_limit_mm: Number(i.diaphragm_sls_drift_limit_mm) || 25,
        stressed_skin_member_restraint: !!i.stressed_skin_member_restraint,
        member_restraint_spacing_m:
          i.diaphragm_member_restraint_spacing_m === '' || i.diaphragm_member_restraint_spacing_m == null
            ? undefined
            : Number(i.diaphragm_member_restraint_spacing_m),
        sn027_bottom_chord_check: !!i.sn027_bottom_chord_check,
        sn027_purlin_I_m4:
          i.sn027_purlin_I_m4 === '' || i.sn027_purlin_I_m4 == null ? undefined : Number(i.sn027_purlin_I_m4),
        sn027_web_I_m4:
          i.sn027_web_I_m4 === '' || i.sn027_web_I_m4 == null ? undefined : Number(i.sn027_web_I_m4),
        sn027_screw_eccentricity_m: Number(i.sn027_screw_eccentricity_m) || 0.19,
        };
      })(),
      analysis_stage: i.analysis_stage || 'completed',
      design_profile: i.design_profile || 'sfs_en',
      solver_mode: 'staged',
      schema_version: 2,
      longitudinal_bracing: {
        transverse_brace_frame_indices: (() => {
          const raw = String(i.transverse_brace_frame_indices || i.vertical_bracing_bays || '').trim();
          if (!raw) return undefined;
          return raw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n));
        })(),
        lengthwise_brace_frame_indices: (() => {
          const raw = String(i.lengthwise_brace_frame_indices || '').trim();
          if (!raw) return undefined;
          return raw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n));
        })(),
        vertical_bracing_bays: (() => {
          const raw = String(i.transverse_brace_frame_indices || i.vertical_bracing_bays || '').trim();
          if (!raw) return undefined;
          return raw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n));
        })(),
      },
    };
  }, [inputs]);

  const loadFromSaved = useCallback((input_data, results_data) => {
    if (input_data && typeof input_data === 'object') {
      const migrated = { ...input_data };
      if (!migrated.schema_version || migrated.schema_version < 2) {
        migrated.schema_version = 2;
        migrated.solver_mode = migrated.solver_mode || 'staged';
        migrated.design_profile = migrated.design_profile || 'sfs_en';
      }
      if (migrated.design_profile === 'nen_en') migrated.design_profile = 'sfs_en';
      if (migrated.national_annex === 'NL') migrated.national_annex = 'FI';
      setInputs((prev) => ({ ...prev, ...migrated }));
    }
    if (results_data && typeof results_data === 'object') {
      setResults(results_data);
      setResultsOutOfDate(false);
    } else {
      setResults(null);
      setResultsOutOfDate(true);
    }
  }, []);

  const value = useMemo(
    () => ({
      inputs,
      setInputs,
      updateInputs,
      results,
      setResults,
      previewGeometry,
      setPreviewGeometry,
      previewBracingLayout,
      setPreviewBracingLayout,
      loading,
      setLoading,
      error,
      setError,
      resultsOutOfDate,
      setResultsOutOfDate,
      buildApiPayload,
      loadFromSaved,
    }),
    [
      inputs,
      updateInputs,
      results,
      previewGeometry,
      previewBracingLayout,
      loading,
      error,
      resultsOutOfDate,
      buildApiPayload,
      loadFromSaved,
    ],
  );

  return <TrussContext.Provider value={value}>{children}</TrussContext.Provider>;
}

export function useTruss() {
  const ctx = useContext(TrussContext);
  if (!ctx) throw new Error('useTruss must be used within TrussProvider');
  return ctx;
}

export default TrussContext;
